import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Icon } from '@iconify/react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import config from '../config';
import MapPicker from './child/MapPicker';
import ImageCropperModal from './common/ImageCropperModal';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB limit for source, we compress output

const AddVenueLayer = ({ venueId = null, onSuccess }) => {
    const { auth } = useAuth();
    const isEditMode = !!venueId;
    
    // Cropper State
    const [cropperState, setCropperState] = useState({
        show: false,
        imageSrc: null,
        initialAspectRatio: 1,
        fixedAspectRatio: null,
        minWidth: 750,
        type: null // 'logo' or 'thumbnail'
    });
    const [formData, setFormData] = useState({
        title: '',
        business_name: '',
        description: '',
        license_number: '',
        phone: '',
        email: '',
        business_category: '', // Keep for backward compatibility
        category_ids: [],      // New: Array of selected category IDs
        primary_category_id: null, // New: Primary category ID
        address: {
            street: '',
            city: '',
            state: '',
            zip: '',
            country: ''
        },
        location: null
    });
    
    // File State
    const [logo, setLogo] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [thumbnail, setThumbnail] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState(null);
    
    // Features State
    const [, setFeatures] = useState([]);
    
    // Categories State
    const [categories, setCategories] = useState([]);
    
    // Subscription Limits
    const [limits, setLimits] = useState(null);
    const [, setUsage] = useState(null);
    
    // UI State
    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(isEditMode);
    const [errors, setErrors] = useState({});

    // Fetch subscription limits
    const fetchLimits = useCallback(async () => {
        try {
            const url = venueId 
                ? `${config.API_BASE_URL}/api/v2/venues/limits?venue_id=${venueId}`
                : `${config.API_BASE_URL}/api/v2/venues/limits`;
            
            const response = await auth.fetch(url);
            const data = await response.json();
            
            if (response.ok) {
                setLimits(data.limits);
                setUsage({
                    gallery: data.limits.gallery.current,
                    blogs: data.limits.blogs.current,
                    features: data.limits.features.current,
                    venues: data.limits.venues.current
                });
            }
        } catch (error) {
            console.error('Error fetching limits:', error);
        }
    }, [auth, venueId]);

    // Fetch categories from API
    const fetchCategories = useCallback(async () => {
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/categories?limit=100`);
            const data = await response.json();
            
            if (response.ok && data.data) {
                setCategories(data.data);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    }, [auth]);

    // Fetch venue data for edit mode
    const fetchVenueData = useCallback(async () => {
        if (!venueId) return;
        
        try {
            setFetchingData(true);
            const response = await auth.fetch(`${config.API_BASE_URL}/api/v2/venues/${venueId}`);
            const data = await response.json();
            
            if (response.ok) {
                // Extract category IDs from response
                const categoryIds = data.categories 
                    ? data.categories.map(c => c.id) 
                    : [];
                const primaryCatId = data.categories 
                    ? data.categories.find(c => c.is_primary)?.id || categoryIds[0] || null
                    : data.primary_category_id || null;

                setFormData({
                    title: data.title || data.business_name || '',
                    business_name: data.business_name || '',
                    description: data.description || '',
                    license_number: data.license_number || '',
                    phone: data.phone || '',
                    email: data.email || '',
                    business_category: data.business_category || '',
                    dm_type: data.dm_type || 'none',
                    dm_value: data.dm_value || '',
                    category_ids: categoryIds,
                    primary_category_id: primaryCatId,
                    address: {
                        street: data.street || '',
                        city: data.city || '',
                        state: data.state || '',
                        zip: data.zip || '',
                        country: data.country || ''
                    },
                    location: (data.location && typeof data.location === 'string') ? JSON.parse(data.location) : (data.location || null)
                });
                
                if (data.logo) setLogoPreview(data.logo);
                if (data.thumbnail) setThumbnailPreview(data.thumbnail);
                if (data.features) setFeatures(data.features);
                
                if (data.limits) {
                    setLimits({
                        gallery: { max: data.limits.galleryLimit, current: data.gallery?.length || 0 },
                        blogs: { max: data.limits.blogLimit, current: data.blogs?.length || 0 },
                        features: { max: data.limits.featureLimit, current: data.features?.length || 0 }
                    });
                }
            } else {
                toast.error(data.message || 'Failed to load venue');
            }
        } catch (error) {
            console.error('Error fetching venue:', error);
            toast.error('Failed to load venue data');
        } finally {
            setFetchingData(false);
        }
    }, [auth, venueId]);

    useEffect(() => {
        fetchLimits();
        fetchCategories();
        if (isEditMode) {
            fetchVenueData();
        }
    }, [fetchLimits, fetchCategories, fetchVenueData, isEditMode]);

    // Clean up object URLs on unmount
    useEffect(() => {
        return () => {
            if (cropperState.imageSrc) {
                URL.revokeObjectURL(cropperState.imageSrc);
            }
        };
    }, [cropperState.imageSrc]);

    // Handle file selection - Opens Cropper
    const handleFileChange = (e, type) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            toast.error('Only JPG, PNG, and WebP formats are allowed');
            e.target.value = '';
            return;
        }

        if (file.size > MAX_FILE_SIZE) {
             toast.error(`File size must be less than ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
             e.target.value = '';
             return;
        }

        const objectUrl = URL.createObjectURL(file);
        const isLogo = type === 'logo';

        setCropperState({
            show: true,
            imageSrc: objectUrl,
            type: type,
            initialAspectRatio: isLogo ? 1 : 3/2,
            fixedAspectRatio: isLogo ? 1 : 3/2, // Enforce locked ratio: 1:1 for Logo, 3:2 for Thumbnail/Header
            minWidth: 750
        });
        
        // Reset input value to allow re-selecting same file
        e.target.value = '';
    };

    const handleCropperHide = useCallback(() => {
        if (cropperState.imageSrc) {
            URL.revokeObjectURL(cropperState.imageSrc);
        }
        setCropperState(prev => ({ ...prev, show: false, imageSrc: null }));
    }, [cropperState.imageSrc]);

    const handleCropComplete = useCallback((croppedFile, croppedPreview) => {
        if (cropperState.type === 'logo') {
            setLogo(croppedFile);
            setLogoPreview(croppedPreview);
        } else {
            setThumbnail(croppedFile);
            setThumbnailPreview(croppedPreview);
        }
        // Modal closes automatically via onHide prop in ImageCropperModal calls handleCropperHide
    }, [cropperState.type]);

    // Handle form field changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        
        if (name.startsWith('address.')) {
            const addressField = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                address: { ...prev.address, [addressField]: value }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    // Handle location selection from map
    const handleLocationSelect = (location, address) => {
        setFormData(prev => ({
            ...prev,
            location: {
                type: 'Point',
                coordinates: [location.lng, location.lat]
            },
            address: address ? {
                street: address.street || prev.address.street,
                city: address.city || prev.address.city,
                state: address.state || prev.address.state,
                zip: address.zip || prev.address.zip,
                country: address.country || prev.address.country
            } : prev.address
        }));
    };

    // Validate form - returns errors object or null if valid
    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.title?.trim()) {
            newErrors.title = 'Title is required';
        } else if (formData.title.length > 120) {
            newErrors.title = 'Title must not exceed 120 characters';
        }
        
        if (formData.description && formData.description.length > 2000) {
            newErrors.description = 'Description must not exceed 2000 characters';
        }
        
        // Validate DM Options
        if (formData.dm_type && formData.dm_type !== 'none' && !formData.dm_value?.trim()) {
            newErrors.dm_value = formData.dm_type === 'whatsapp' 
                ? 'WhatsApp number is required' 
                : 'Instagram username is required';
        }
        
        if (!formData.address.street?.trim()) {
            newErrors['address.street'] = 'Street address is required';
        }
        if (!formData.address.city?.trim()) {
            newErrors['address.city'] = 'City is required';
        }
        if (!formData.address.country?.trim()) {
            newErrors['address.country'] = 'Country is required';
        }
        
        // Check venue limit for new venues
        if (!isEditMode && limits?.venues) {
            if (limits.venues.current >= limits.venues.max) {
                newErrors.general = `You have reached the maximum number of venues (${limits.venues.max}) allowed by your plan.`;
            }
        }
        
        setErrors(newErrors);
        
        // Return the errors for immediate use
        if (Object.keys(newErrors).length > 0) {
            return newErrors;
        }
        return null;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const validationErrors = validateForm();
        if (validationErrors) {
            // Show specific error messages
            const errorList = Object.values(validationErrors);
            if (errorList.length === 1) {
                toast.error(errorList[0]);
            } else {
                toast.error(`Missing required fields: ${errorList.slice(0, 3).join(', ')}${errorList.length > 3 ? '...' : ''}`);
            }
            return;
        }

        setLoading(true);
        
        try {
            const formDataToSend = new FormData();
            
            // Add text fields
            formDataToSend.append('title', formData.title);
            formDataToSend.append('business_name', formData.business_name || formData.title);
            formDataToSend.append('description', formData.description);
            formDataToSend.append('license_number', formData.license_number);
            formDataToSend.append('phone', formData.phone);
            formDataToSend.append('email', formData.email);
            formDataToSend.append('business_category', formData.business_category);
            formDataToSend.append('dm_type', formData.dm_type);
            formDataToSend.append('dm_value', formData.dm_value);
            
            // Add category data
            if (formData.category_ids && formData.category_ids.length > 0) {
                formDataToSend.append('category_ids', JSON.stringify(formData.category_ids));
                formDataToSend.append('primary_category_id', formData.primary_category_id || formData.category_ids[0]);
            }
            
            // Add structured address as JSON
            formDataToSend.append('address', JSON.stringify(formData.address));
            
            // Add location as JSON
            if (formData.location) {
                formDataToSend.append('location', JSON.stringify(formData.location));
            }
            
            // Add files
            if (logo) formDataToSend.append('logo', logo);
            if (thumbnail) formDataToSend.append('thumbnail', thumbnail);
            
            const url = isEditMode 
                ? `${config.API_BASE_URL}/api/v2/venues/${venueId}`
                : `${config.API_BASE_URL}/api/v2/venues`;
            
            const response = await auth.fetch(url, {
                method: isEditMode ? 'PUT' : 'POST',
                body: formDataToSend,
                headers: {} // Let browser set Content-Type for FormData
            });
            
            // Check content type before parsing
            const contentType = response.headers.get('content-type');
            let data;
            if (contentType && contentType.includes('application/json')) {
                data = await response.json();
            } else {
                const text = await response.text();
                console.error('Server returned non-JSON response:', text);
                data = { error: 'SERVER_ERROR', message: 'Server returned an invalid response' };
            }
            
            if (response.ok) {
                toast.success(isEditMode ? 'Venue updated successfully!' : 'Venue created successfully!');
                if (onSuccess) onSuccess(data);
            } else {
                if (data.error === 'SUBSCRIPTION_LIMIT_EXCEEDED') {
                    toast.error(data.message);
                } else if (data.errors) {
                    const errorMessages = data.errors.map(e => `${e.field}: ${e.message}`).join(', ');
                    toast.error(errorMessages);
                } else {
                    toast.error(data.message || 'Failed to save venue');
                }
            }
        } catch (error) {
            console.error('Error saving venue:', error);
            toast.error('An error occurred while saving the venue');
        } finally {
            setLoading(false);
        }
    };

    // Calculate remaining slots
    // const getRemainingSlots = (type) => {
    //     if (!limits || !limits[type]) return null;
    //     return Math.max(0, limits[type].max - limits[type].current);
    // };

    if (fetchingData) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <h6 className="text-lg fw-semibold mb-0">
                    {isEditMode ? 'Edit Venue' : 'Add New Venue'}
                </h6>
            </div>
            
            <div className="card-body p-24">
                {/* Subscription Limits Banner */}
                {limits && (
                    <div className="alert alert-info mb-24 d-flex flex-wrap gap-3 align-items-center">
                        <Icon icon="mdi:information-outline" className="text-xl" />
                        <span className="fw-medium">Your Plan Limits:</span>
                        <span className="badge bg-primary-600 px-12 py-6">
                            Venues: {limits.venues?.current || 0}/{limits.venues?.max || 0}
                        </span>
                        {venueId && (
                            <>
                                <span className="badge bg-info-600 px-12 py-6">
                                    Gallery: {limits.gallery?.current || 0}/{limits.gallery?.max || 0}
                                </span>
                                <span className="badge bg-success-600 px-12 py-6">
                                    Blogs: {limits.blogs?.current || 0}/{limits.blogs?.max || 0}
                                </span>
                                <span className="badge bg-warning-600 px-12 py-6">
                                    Features: {limits.features?.current || 0}/{limits.features?.max || 0}
                                </span>
                            </>
                        )}
                    </div>
                )}

                {errors.general && (
                    <div className="alert alert-danger mb-24">
                        <Icon icon="mdi:alert-circle" className="me-2" />
                        {errors.general}
                    </div>
                )}
                
                <form onSubmit={handleSubmit}>
                    {/* Basic Information */}
                    <div className="row gy-3 mb-24">
                        <div className="col-12">
                            <h6 className="text-md fw-semibold mb-16 border-bottom pb-8">
                                Basic Information
                            </h6>
                        </div>
                        
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Title <span className="text-danger">*</span>
                                <small className="text-secondary-light ms-2">
                                    ({formData.title.length}/120)
                                </small>
                            </label>
                            <input
                                type="text"
                                className={`form-control radius-8 ${errors.title ? 'is-invalid' : ''}`}
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                maxLength={120}
                                placeholder="Enter venue title"
                            />
                            {errors.title && <div className="invalid-feedback">{errors.title}</div>}
                        </div>
                        
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Business Name
                            </label>
                            <input
                                type="text"
                                className="form-control radius-8"
                                name="business_name"
                                value={formData.business_name}
                                onChange={handleChange}
                                placeholder="Legal business name"
                            />
                        </div>
                        
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Categories
                                <small className="text-secondary-light ms-2">(select multiple)</small>
                            </label>
                            <div className="border radius-8 p-12 bg-base" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                {categories.length === 0 ? (
                                    <p className="text-secondary-light mb-0 text-sm">Loading categories...</p>
                                ) : (
                                    categories.map((category) => (
                                        <div key={category.id} className="form-check d-flex align-items-center gap-8 mb-8">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id={`cat-${category.id}`}
                                                checked={formData.category_ids.includes(category.id)}
                                                onChange={(e) => {
                                                    const catId = category.id;
                                                    if (e.target.checked) {
                                                        // Add category
                                                        const newIds = [...formData.category_ids, catId];
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            category_ids: newIds,
                                                            business_category: category.slug || category.name,
                                                            // Set as primary if first
                                                            primary_category_id: prev.primary_category_id || catId
                                                        }));
                                                    } else {
                                                        // Remove category
                                                        const newIds = formData.category_ids.filter(id => id !== catId);
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            category_ids: newIds,
                                                            // Update primary if removed
                                                            primary_category_id: prev.primary_category_id === catId 
                                                                ? newIds[0] || null 
                                                                : prev.primary_category_id
                                                        }));
                                                    }
                                                }}
                                            />
                                            <label className="form-check-label text-sm" htmlFor={`cat-${category.id}`}>
                                                {category.name}
                                            </label>
                                            {formData.category_ids.includes(category.id) && (
                                                <button
                                                    type="button"
                                                    className={`btn btn-sm py-2 px-8 ms-auto ${
                                                        formData.primary_category_id === category.id 
                                                            ? 'btn-primary-600 text-white' 
                                                            : 'btn-outline-secondary-600'
                                                    }`}
                                                    onClick={() => {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            primary_category_id: category.id,
                                                            business_category: category.slug || category.name
                                                        }));
                                                    }}
                                                    title="Set as primary category"
                                                >
                                                    {formData.primary_category_id === category.id ? '★ Primary' : 'Set Primary'}
                                                </button>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                            {formData.category_ids.length > 0 && (
                                <small className="text-secondary-light mt-4 d-block">
                                    Selected: {formData.category_ids.length} | 
                                    Primary: {categories.find(c => c.id === formData.primary_category_id)?.name || 'None'}
                                </small>
                            )}
                        </div>
                        
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                License Number
                            </label>
                            <input
                                type="text"
                                className="form-control radius-8"
                                name="license_number"
                                value={formData.license_number}
                                onChange={handleChange}
                                placeholder="Business license number"
                            />
                        </div>
                        
                        <div className="col-12">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Description
                                <small className="text-secondary-light ms-2">
                                    ({formData.description.length}/2000)
                                </small>
                            </label>
                            <textarea
                                className={`form-control radius-8 ${errors.description ? 'is-invalid' : ''}`}
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows={4}
                                maxLength={2000}
                                placeholder="Describe your venue..."
                            />
                            {errors.description && <div className="invalid-feedback">{errors.description}</div>}
                        </div>
                    </div>
                    
                    {/* Media Upload */}
                    <div className="row gy-3 mb-24">
                        <div className="col-12">
                            <h6 className="text-md fw-semibold mb-16 border-bottom pb-8">
                                Media
                            </h6>
                        </div>
                        
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Logo
                                <small className="text-secondary-light ms-2">(Max 2MB, JPG/PNG/WebP)</small>
                            </label>
                            <div className="d-flex align-items-center gap-3">
                                {logoPreview && (
                                    <img
                                        src={logoPreview.startsWith('data:') || logoPreview.startsWith('blob:') ? logoPreview : `${config.API_BASE_URL}/${logoPreview}`}
                                        alt="Logo preview"
                                        className="w-80-px h-80-px rounded-circle object-fit-cover border"
                                    />
                                )}
                                <div className="flex-grow-1">
                                    <input
                                        type="file"
                                        className="form-control radius-8"
                                        accept=".jpg,.jpeg,.png,.webp"
                                        onChange={(e) => handleFileChange(e, 'logo')}
                                    />
                                </div>
                            </div>
                        </div>
                        
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Thumbnail
                                <small className="text-secondary-light ms-2">(Max 10MB source, optimized output)</small>
                            </label>
                            <div className="d-flex align-items-center gap-3">
                                {thumbnailPreview && (
                                    <img
                                        src={thumbnailPreview.startsWith('data:') || thumbnailPreview.startsWith('blob:') ? thumbnailPreview : `${config.API_BASE_URL}/${thumbnailPreview}`}
                                        alt="Thumbnail preview"
                                        className="w-120-px h-80-px rounded-8 object-fit-cover border"
                                    />
                                )}
                                <div className="flex-grow-1">
                                    <input
                                        type="file"
                                        className="form-control radius-8"
                                        accept=".jpg,.jpeg,.png,.webp"
                                        onChange={(e) => handleFileChange(e, 'thumbnail')}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Contact Information */}
                    <div className="row gy-3 mb-24">
                        <div className="col-12">
                            <h6 className="text-md fw-semibold mb-16 border-bottom pb-8">
                                Contact Information
                            </h6>
                        </div>
                        
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Phone
                            </label>
                            <input
                                type="tel"
                                className="form-control radius-8"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="+1 234 567 8900"
                            />
                        </div>
                        
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Email
                            </label>
                            <input
                                type="email"
                                className="form-control radius-8"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="venue@example.com"
                            />
                        </div>
                    </div>
                    
                    {/* Direct Messaging Options */}
                    <div className="row gy-3 mb-24">
                        <div className="col-12">
                            <h6 className="text-md fw-semibold mb-16 border-bottom pb-8">
                                Direct Messaging Options
                            </h6>
                        </div>
                        
                        <div className="col-12">
                            <div className="d-flex flex-wrap gap-4">
                                <div className="d-flex align-items-center gap-2 me-3">
                                    <input
                                        className="form-check-input mt-0"
                                        type="radio"
                                        name="dm_type"
                                        id="dm_none"
                                        value="none"
                                        checked={formData.dm_type === 'none'}
                                        onChange={(e) => {
                                            setFormData(prev => ({ ...prev, dm_type: 'none', dm_value: '' }));
                                        }}
                                    />
                                    <label className="form-check-label cursor-pointer mb-0" htmlFor="dm_none">
                                        None
                                    </label>
                                </div>
                                <div className="d-flex align-items-center gap-2 me-3">
                                    <input
                                        className="form-check-input mt-0"
                                        type="radio"
                                        name="dm_type"
                                        id="dm_whatsapp"
                                        value="whatsapp"
                                        checked={formData.dm_type === 'whatsapp'}
                                        onChange={(e) => {
                                            setFormData(prev => ({ ...prev, dm_type: 'whatsapp', dm_value: '' }));
                                        }}
                                    />
                                    <label className="form-check-label d-flex align-items-center gap-2 cursor-pointer mb-0" htmlFor="dm_whatsapp">
                                        <Icon icon="logos:whatsapp-icon" width="20" /> WhatsApp
                                    </label>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <input
                                        className="form-check-input mt-0"
                                        type="radio"
                                        name="dm_type"
                                        id="dm_instagram"
                                        value="instagram"
                                        checked={formData.dm_type === 'instagram'}
                                        onChange={(e) => {
                                            setFormData(prev => ({ ...prev, dm_type: 'instagram', dm_value: '' }));
                                        }}
                                    />
                                    <label className="form-check-label d-flex align-items-center gap-2 cursor-pointer mb-0" htmlFor="dm_instagram">
                                        <Icon icon="skill-icons:instagram" width="20" /> Instagram
                                    </label>
                                </div>
                            </div>
                        </div>

                        {formData.dm_type !== 'none' && (
                            <div className="col-md-6">
                                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                    {formData.dm_type === 'whatsapp' ? 'WhatsApp Number' : 'Instagram Username'}
                                    <span className="text-danger">*</span>
                                </label>
                                <div className="input-group">
                                    <span className="input-group-text bg-base">
                                        <Icon 
                                            icon={formData.dm_type === 'whatsapp' ? 'ph:phone' : 'ph:instagram-logo'} 
                                            className="text-xl text-secondary-light" 
                                        />
                                    </span>
                                    <input
                                        type="text"
                                        className={`form-control radius-8 ${errors.dm_value ? 'is-invalid' : ''}`}
                                        name="dm_value"
                                        value={formData.dm_value}
                                        onChange={handleChange}
                                        placeholder={formData.dm_type === 'whatsapp' ? '+971 50 123 4567' : 'nexusdesignstudios'}
                                    />
                                    {errors.dm_value && <div className="invalid-feedback">{errors.dm_value}</div>}
                                </div>
                                <div className="form-text text-sm mt-1 text-secondary-light">
                                    {formData.dm_type === 'whatsapp' 
                                        ? 'Enter full number with country code (e.g. +971...)' 
                                        : 'Username or ig.me link (e.g. https://ig.me/m/nexusdesignstudios)'}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Address */}
                    <div className="row gy-3 mb-24">
                        <div className="col-12">
                            <h6 className="text-md fw-semibold mb-16 border-bottom pb-8">
                                Address
                            </h6>
                        </div>
                        
                        <div className="col-12">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Street <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                className={`form-control radius-8 ${errors['address.street'] ? 'is-invalid' : ''}`}
                                name="address.street"
                                value={formData.address.street}
                                onChange={handleChange}
                                placeholder="123 Main Street"
                            />
                            {errors['address.street'] && <div className="invalid-feedback">{errors['address.street']}</div>}
                        </div>
                        
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                City <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                className={`form-control radius-8 ${errors['address.city'] ? 'is-invalid' : ''}`}
                                name="address.city"
                                value={formData.address.city}
                                onChange={handleChange}
                                placeholder="City"
                            />
                            {errors['address.city'] && <div className="invalid-feedback">{errors['address.city']}</div>}
                        </div>
                        
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                State / Province
                            </label>
                            <input
                                type="text"
                                className="form-control radius-8"
                                name="address.state"
                                value={formData.address.state}
                                onChange={handleChange}
                                placeholder="State"
                            />
                        </div>
                        
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                ZIP / Postal Code
                            </label>
                            <input
                                type="text"
                                className="form-control radius-8"
                                name="address.zip"
                                value={formData.address.zip}
                                onChange={handleChange}
                                placeholder="12345"
                            />
                        </div>
                        
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                                Country <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                className={`form-control radius-8 ${errors['address.country'] ? 'is-invalid' : ''}`}
                                name="address.country"
                                value={formData.address.country}
                                onChange={handleChange}
                                placeholder="Country"
                            />
                            {errors['address.country'] && <div className="invalid-feedback">{errors['address.country']}</div>}
                        </div>
                    </div>
                    
                    {/* Map Location */}
                    <div className="row gy-3 mb-24">
                        <div className="col-12">
                            <h6 className="text-md fw-semibold mb-16 border-bottom pb-8">
                                Map Location
                            </h6>
                            <p className="text-secondary-light text-sm mb-16">
                                Click on the map to set your venue's location. Address fields will be auto-filled but can be manually overridden.
                            </p>
                        </div>
                        
                        <div className="col-12">
                            <div className="border radius-8 overflow-hidden" style={{ height: '400px' }}>
                                <MapPicker
                                    initialLocation={formData.location?.coordinates 
                                        ? { lat: formData.location.coordinates[1], lng: formData.location.coordinates[0] }
                                        : null
                                    }
                                    onLocationSelect={handleLocationSelect}
                                />
                            </div>
                            {formData.location && (
                                <div className="mt-8 text-sm text-secondary-light">
                                    <Icon icon="mdi:map-marker" className="me-1" />
                                    Coordinates: {formData.location.coordinates[1].toFixed(6)}, {formData.location.coordinates[0].toFixed(6)}
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {/* Submit Button */}
                    <div className="d-flex justify-content-end gap-3 pt-16 border-top">
                        <button
                            type="button"
                            className="btn btn-outline-secondary radius-8 px-24"
                            onClick={() => window.history.back()}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary radius-8 px-24"
                            disabled={loading || (limits?.venues && !isEditMode && limits.venues.current >= limits.venues.max)}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Icon icon="mdi:content-save" className="me-2" />
                                    {isEditMode ? 'Update Venue' : 'Create Venue'}
                                </>
                            )}
                        </button>
                    </div>
                    
                    {/* Image Cropper Modal */}
                    {cropperState.show && (
                        <ImageCropperModal
                            show={cropperState.show}
                            onHide={handleCropperHide}
                            imageSrc={cropperState.imageSrc}
                            onCropComplete={handleCropComplete}
                            fixedAspectRatio={cropperState.fixedAspectRatio}
                            initialAspectRatio={cropperState.initialAspectRatio}
                            minWidth={cropperState.minWidth}
                        />
                    )}
                </form>
            </div>
        </div>
    );
};

export default AddVenueLayer;
